import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { useApolloClient } from '@apollo/client/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebounce } from '@hooks/useDebounce';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { EventTypes, emitter } from '@services/emitter';

import { navigateRemoteAppointment } from './query';

// calling `mutate` on the apollo client directly will avoid state updates
// occurring, which is the case with the `useMutation` hook. These state
// changes, such as `loading`, will cause the hook to reload and thus re-render
// the component it is implemented in. This can cause render-loops thusly:
// 1. Component calls mutation
// 2. mutation hook updates state
// 3. update to state causes hook to reload
// 4. hook reload causes Component to re-render
// 5. GOTO 1.
const useSetPath = (appointmentId: string) => {
  const apolloClient = useApolloClient();

  return (path: string) =>
    apolloClient.mutate({
      mutation: navigateRemoteAppointment,
      variables: {
        id: appointmentId,
        path,
      },
    });
};

const useSyncPath = () => {
  const setSnackbarError = useSnackbarError();
  const { appointment, isSyncedAppointment, hasControl } =
    useAppointmentContext();
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const setPath = useSetPath(appointment?.id ?? '');
  const debouncedSetPath = useDebounce(setPath, 250);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isSyncedAppointment) return;
    const handleNavUpdate = (path: string) => {
      navigate(path);
    };
    emitter.on(EventTypes.RemoteNavigation, handleNavUpdate);

    return () => {
      emitter.off(EventTypes.RemoteNavigation, handleNavUpdate);
    };
  }, [navigate, isSyncedAppointment]);

  return async (path: string) => {
    if (!isSyncedAppointment) return;
    if (path === currentPath) return;
    if (!appointment?.id) return;
    if (appointment.path === path) {
      setCurrentPath(path);
      return;
    }

    if (!hasControl) {
      if (appointment.path && appointment.path !== path) {
        navigate(appointment.path);
      }
      setCurrentPath(path);
      return;
    }

    try {
      await debouncedSetPath(path);
    } catch (error) {
      if (CombinedGraphQLErrors.is(error)) {
        const code = error.errors?.[0]?.extensions?.code;
        if (code === 'AUTHENTICATION_ERROR') return;
      }
      setSnackbarError(error as ErrorLike);
    }

    setCurrentPath(path);
  };
};

export { useSyncPath };
